import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { catchError, combineLatest, from, of, switchMap } from 'rxjs';
import { CampusUserGroup } from '../../CampusUserGroup.js';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '../../master-data-management/users/entities/Student.js';
import { CampusEvent } from '../entities/CampusEvent.js';
import { CampusEventFilter } from '../entities/CampusEventFilter.js';
import { CampusLesson } from '../entities/CampusLesson.js';
import { CampusSection } from '../entities/CampusSection.js';
import { CampusSectionFilter } from '../entities/CampusSectionFilter.js';
import { manipulationAPI } from './contentManipulation.js';
import { levumiAPI } from './levumiAPI.js';
import { validateCampusExercise } from './validateExercises.js';

const resolveSubscribeCampusEventsByFilter = (skip: number, limit: number, filter: CampusEventFilter) => {
  return (gqlFields: BaseQLSelectionSet<CampusEvent>) => {
    if (!filter) return of([] as CampusEvent[]);
    const selector = filter.getMongoFilter();
    const handle = getCollectionHandle<CampusEvent>(CampusEvent._collectionName);
    const handleGroups = getCollectionHandle<CampusUserGroup>(CampusUserGroup._collectionName);
    return combineLatest([from(publishFilter(CampusEvent._class)), handleGroups.watchQuery({})]).pipe(
      switchMap(([publishFilter, watchGroups]) => {
        return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
          ...filter.getMongoOptions().sort,
          skip,
          limit,
        });
      }),
      switchMap(async docs => {
        // If called for specific IDs ignore filtering
        if (filter.eventIDs && filter.eventIDs.length > 0) return docs;

        let filteredDocs: CampusEvent[] = [];

        // Filter for user
        if (filter.forUserID) {
          // Get Groups
          const groupsCollection = await (await getCollection(CampusUserGroup._class))
            .find({ assignedEventIDs: { $exists: true, $type: 'array', $ne: [] } })
            .toArray();
          const groups = groupsCollection.map(d => constructValue(d) as CampusUserGroup);

          // Get User
          const userCollection = await (await getCollection(ChancenwerkUser._class))
            .find({ _id: filter.forUserID })
            .toArray();
          const user = userCollection.map(d => constructValue(d) as CampusUserGroup)[0];

          if (!user) {
            throw new Error('Couldnt find logged in user');
          }

          // Filter Studentevents: First: assigned groups, second: grade
          if (user._class === Student._class) {
            const student = user as unknown as Student;
            // Filter groups
            for (const doc of docs) {
              if (groups.some(g => g.assignedEventIDs?.includes(doc._id))) filteredDocs.push(doc);
            }
            // Filter the remaining events for grade if the user is a student
            filteredDocs = filteredDocs.concat(
              docs.filter(
                event =>
                  event.forGrades.includes(student.schoolInformation.jahrgangsstufe ?? 0) &&
                  !filteredDocs.includes(event),
              ),
            );
            return filteredDocs;
          } else {
            return docs.filter(event => !groups.some(g => g.assignedEventIDs?.includes(event._id)));
          }
        } else {
          return docs;
        }
      }),
      catchError(err => {
        logger.error({ err }, 'Error during subscription');
        return of([] as CampusEvent[]);
      }),
    );
  };
};

const subscribeCampusEventsByFilter = registerSubscription({
  type: () => [CampusEvent],
  operationName: 'subscribeCampusEventsByFilter',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => CampusEventFilter, name: 'filter' },
  ],
  resolve: resolveSubscribeCampusEventsByFilter,
});

const resolveGetLessonForExercise = (exerciseID: string) => {
  return async (gqlFields: BaseQLSelectionSet<CampusLesson>) => {
    const col = await getCollection<CampusLesson>(CampusLesson._class);
    const lesson = await col.findOne<CampusLesson>({ exerciseIDs: exerciseID });
    if (!lesson) return undefined;
    return lesson;
  };
};

const queryGetLessonForExercise = registerQuery({
  type: () => CampusLesson,
  operationName: 'getLessonForExercise',
  params: [{ type: () => String, name: 'exerciseID' }],
  resolve: resolveGetLessonForExercise,
});

const resolveGetSectionForLesson = (lessonID: string, filterOutHidden?: boolean) => {
  return async (gqlFields: BaseQLSelectionSet<CampusSection>) => {
    const col = await getRawCollection<CampusSection>(CampusSection._collectionName);
    const section = await col.findOne<CampusSection>({ lessonIDs: lessonID });
    if (!section) return null;

    if (filterOutHidden) {
      const filteredLessons: string[] = [];
      const col = await getCollection<CampusLesson>(CampusLesson._class);
      if (!section.lessonIDs) throw new Error('expecting lessonIDs to exist on respective section');
      for (let i = 0; i < section.lessonIDs.length; i++) {
        const lessonID = section.lessonIDs[i];
        if (!lessonID) continue;
        const lesson = await col.findOne<CampusLesson>({ _id: lessonID });
        if (!lesson) continue;
        if (lesson.hiddenFromStudents) continue;
        filteredLessons.push(lessonID);
      }
      section.lessonIDs = filteredLessons;
    }

    return section;
  };
};

const queryGetSectionForLesson = registerQuery({
  type: () => CampusSection,
  operationName: 'getSectionForLesson',
  params: [
    { type: () => String, name: 'lessonID' },
    { type: () => Boolean, name: 'filterOutHidden' },
  ],
  resolve: resolveGetSectionForLesson,
});

function searchAggregation(docs: any[], filter: CampusSectionFilter, skip: number, limit: number) {
  const pipeline: any[] = [
    { $match: { _id: { $in: docs.map(d => d._id) } } },

    {
      $lookup: {
        from: 'campus-lessons',
        localField: 'lessonIDs',
        foreignField: '_id',
        as: 'lessons',
      },
    },
    {
      $match: {
        $or: [
          {
            name: { $regex: filter.userSearchText, $options: 'i' },
          },
          {
            'lessons.name': { $regex: filter.userSearchText, $options: 'i' },
          },
        ],
      },
    },
    { $skip: skip },
    { $limit: limit },
  ];

  return pipeline;
}

const resolveSubscribeCampusSectionsByFilter = (skip: number, limit: number, filter: CampusSectionFilter) => {
  return (gqlFields: BaseQLSelectionSet<CampusSection>) => {
    if (!filter) return of([] as CampusSection[]);
    const selector = filter.getMongoFilter();
    const handle = getCollectionHandle<CampusSection>(CampusSection._collectionName);
    return from(publishFilter(CampusSection._class)).pipe(
      switchMap(publishFilter => {
        return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
          sort: { _id: -1 },
        });
      }),
      switchMap(async docs => {
        let filteredLessonIDs: string[] = [];

        // get the users bookmarked lessons
        const userCollection = await getRawCollection<EntityData<ChancenwerkUser>>(ChancenwerkUser._collectionName);
        const user = await userCollection.findOne({ _id: filter.userID });

        if (filter.showAssignedLessons) {
          // get groups to show assigned lesson
          const groupsCollection = await getRawCollection<CampusUserGroup>(CampusUserGroup._collectionName);
          const assignedGroups = await groupsCollection.find({ userIDs: filter.userID }).toArray();

          let newDocs: CampusSection[] = [];
          for (const group of assignedGroups) {
            filteredLessonIDs = [...filteredLessonIDs, ...(group.assignedLessonsIDs ?? [])];

            for (const doc of docs) {
              if (doc.lessonIDs?.some(l => group.assignedLessonsIDs?.includes(l))) {
                newDocs.push(doc);
              }
            }
          }

          if (user?.savedCampusSectionIDs?.length) {
            const savedSections = user.savedCampusSectionIDs
              .map(savedID => {
                const section = docs.find(section => section._id === savedID);
                return section;
              })
              .filter(section => section !== undefined) as CampusSection[];
            newDocs = newDocs.concat(savedSections);
          }

          // Dedupe sections
          docs = Array.from(
            new Set(newDocs.map(obj => obj._id)),
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ).map(id => newDocs.find(obj => obj._id === id)!);
        }

        const collection = await getRawCollection(CampusSection._collectionName);
        const pipeline = searchAggregation(docs, filter, skip, limit);
        const col = (await collection.aggregate(pipeline).toArray()) as CampusSection[];

        // filter lessons from section to show only assigned ones
        if (filter.showAssignedLessons) {
          for (const section of col) {
            // if section is in bookmark, dont filter the lessons
            if (user?.savedCampusSectionIDs?.includes(section._id)) {
              continue;
            }

            section.lessonIDs = section.lessonIDs?.filter(l => filteredLessonIDs.includes(l)) ?? [];
            section.lessons = section.lessons?.filter(l => filteredLessonIDs.includes(l._id)) ?? [];
          }
        }

        return col;
      }),
      catchError(e => {
        return of([] as CampusSection[]);
      }),
    );
  };
};

const subscribeCampusSectionsByFilter = registerSubscription({
  type: () => [CampusSection],
  operationName: 'subscribeCampusSectionsByFilter',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => CampusSectionFilter, name: 'filter' },
  ],
  resolve: resolveSubscribeCampusSectionsByFilter,
});

export namespace LearningContentAPI {
  export const submitTextExercise = validateCampusExercise;
  export const levumi = levumiAPI;
  export const subscribeEventsByFilter = subscribeCampusEventsByFilter;
  export const getLessonForExercise = queryGetLessonForExercise;
  export const getSectionForLesson = queryGetSectionForLesson;
  export const subscribeSectionsByFilter = subscribeCampusSectionsByFilter;
  export const manipulation = manipulationAPI;
}
